import { Search2Icon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPaperPlane, FaSearch } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { SupplierFilterBar } from "../../../common/organization/supplier-filters/SupplierFilterBar";
import {
  isFilteringSuppliers,
  NO_SUPPLIER_FILTERS,
  type SupplierFilters,
  supplierFiltersFromParams,
  writeSupplierFiltersToParams,
} from "../../../common/organization/supplier-filters/supplierFilters";
import { useOrganizationSearch } from "../../../common/organization/useOrganizationSearch";
import { SEARCH_MAX_LENGTH } from "../../../common/entity-table/columnFilters";
import { Paging } from "../../../common/Paging";
import { Invites } from "../../email-invites/manage-supplier-invites/Invites";
import { SearchResults } from "./SearchResults";

export const OrganizationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSendInvites, setShowSendInvites] = useState(false);
  const authState = useLoggedInWithOrgContextState();
  const { t } = useTranslation();

  const name = (searchParams.get("name") ?? "").slice(0, SEARCH_MAX_LENGTH);
  const skip = Number(searchParams.get("skip") ?? 0);
  const limit = Number(searchParams.get("limit") ?? 20);
  const supplierFilters = useMemo(() => supplierFiltersFromParams(searchParams), [searchParams]);
  const filtering = isFilteringSuppliers(supplierFilters);

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) =>
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          mutate(params);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const setName = (value: string) =>
    updateParams((params) => {
      if (value) params.set("name", value);
      else params.delete("name");
      params.delete("skip");
    });
  const setSupplierFilters = (filters: SupplierFilters) =>
    updateParams((params) => {
      writeSupplierFiltersToParams(filters, params);
      params.delete("skip");
    });
  const setSkip = (value: number) => updateParams((params) => params.set("skip", `${value}`));
  const resetFilters = () => setSupplierFilters(NO_SUPPLIER_FILTERS);

  const { currentData, isSearching, isError, refetch } = useOrganizationSearch({
    name,
    limit,
    skip,
    excludeLinkedEntries: true,
    matchOrganizationNumber: true,
    supplierFilters,
    withRelations: true,
  });
  const searchFailed = isError && !isSearching;
  const summariseFilters = () => {
    if (!filtering) return null;
    if (isSearching) return t("Searching …");
    if (searchFailed) return t("Could not load companies. Try again.");
    if (!currentData) return null;
    if (currentData.count === 0) return t("No companies match the search and filters.");
    return t("{{count}} companies found", { count: currentData.count });
  };
  const filterSummary = summariseFilters();

  const body = (
    <Box width="full">
      <VisuallyHidden as="h1">{t("Companies")}</VisuallyHidden>
      <Grid gridTemplateColumns={"1fr"} gridTemplateRows={"auto minmax(0,1fr)"}>
        <GridItem borderBottom={"1px solid"} borderColor="smBorder" py="4">
          <Flex width="full" gap="2" align="flex-start" direction={{ base: "column", md: "row" }}>
            <Box flex="1" minW="0" width="full">
              <SupplierFilterBar
                value={supplierFilters}
                onChange={setSupplierFilters}
                size="md"
                search={
                  <Flex flex="1" minW="0" gap="2">
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Search2Icon color="smMuted" />
                      </InputLeftElement>
                      <Input
                        type="text"
                        placeholder={t("Search for company name or organization number") ?? ""}
                        aria-label={t("Search for company name or organization number") ?? ""}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </InputGroup>
                    <IconButton
                      width="60px"
                      flexShrink={0}
                      aria-label={t("Search")}
                      variant={"solid"}
                      colorScheme="teal"
                      icon={<Icon as={FaSearch} w="15px" h="15px" />}
                    />
                  </Flex>
                }
              />
            </Box>
            {authState.selectedOrg.canManageSupplierInvites && (
              <Button
                flexShrink={0}
                leftIcon={<Icon as={FaPaperPlane} w="15px" h="15px" />}
                paddingLeft="20px"
                paddingRight="20px"
                variant={"solid"}
                colorScheme="teal"
                onClick={() => setShowSendInvites(true)}
              >
                {t("Invites")}
              </Button>
            )}
          </Flex>
          <Flex role="status" aria-live="polite" align="center" gap="2" mt="2">
            {filterSummary && (
              <>
                {isSearching && <Spinner size="xs" />}
                <Text fontSize="sm" fontWeight="semibold" color={searchFailed ? "smError" : "smMuted"}>
                  {filterSummary}
                </Text>
                {searchFailed && (
                  <Button size="sm" variant="link" colorScheme="teal" onClick={() => refetch()}>
                    {t("Try again")}
                  </Button>
                )}
              </>
            )}
          </Flex>
        </GridItem>
        <GridItem>
          <Box marginTop={"9px"}>
            <Paging
              limit={limit}
              skip={skip}
              setSkip={setSkip}
              isLoading={isSearching}
              count={currentData?.count}
            />
            {searchFailed ? (
              !filtering && (
                <Flex align="center" gap="3" ml="4" mt="3">
                  <Text fontSize="sm" color="smError">
                    {t("Could not load companies. Try again.")}
                  </Text>
                  <Button size="sm" variant="outline" onClick={() => refetch()}>
                    {t("Try again")}
                  </Button>
                </Flex>
              )
            ) : (
              <SearchResults
                organizations={currentData?.organizations}
                relations={currentData?.relations}
                isSearching={isSearching}
                count={currentData?.count}
                onResetFilters={filtering ? resetFilters : undefined}
                onClearSearch={name ? () => setName("") : undefined}
                emptyMessageShownAbove={filtering}
              />
            )}
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );

  return (
    <>
      {showSendInvites && <Invites isOpen={true} onClose={() => setShowSendInvites(false)} />}
      {body}
    </>
  );
};
